import { api, withIdempotencyKey } from './client'

/**
 * F-071 (TASK-0325): client dei modelli documentali (`DocumentTemplatesController`,
 * `/api/document-templates`) e della generazione dalla pratica (`ContractDocumentGenerationController`,
 * `/api/contracts/{id}/generated-documents`). I modelli si leggono dal backoffice e si scrivono solo
 * da Admin (403 agli altri). Un modello nasce in Bozza, si modifica solo in Bozza e da Attivo e'
 * immutabile: per cambiarlo si crea una nuova versione. Il documento generato e' HTML stampabile,
 * non PDF, e conserva codice e versione del modello.
 */
export type DocumentTemplateStatus = 'Bozza' | 'Attivo' | 'Archiviato'
export type DocumentTemplateSegment = 'consumer' | 'business'

export interface DocumentMergeField {
  code: string
  label: string
  group: string
}

export interface DocumentTemplate {
  id: number
  code: string
  name: string
  kind: string
  version: number
  status: DocumentTemplateStatus
  departmentId: number | null
  operatorId: number | null
  productId: number | null
  customerSegment: DocumentTemplateSegment | null
  language: string
  validFrom: string | null
  validTo: string | null
  /** Solo nel dettaglio (`GET {id}`), assente nell'elenco. */
  bodyHtml: string | null
  requiredFields: string[]
  activatedBy: number | null
  activatedAt: string | null
  changeReason: string | null
  createdBy: number | null
  createdAt: string
  updatedAt: string | null
}

export interface DocumentTemplateBody {
  code: string
  name: string
  kind: string
  departmentId: number | null
  operatorId: number | null
  productId: number | null
  customerSegment: DocumentTemplateSegment | null
  language: string
  validFrom: string | null
  validTo: string | null
  bodyHtml: string
  requiredFields: string[]
  changeReason: string | null
}

export interface GeneratedDocument {
  id: number
  name: string
  kind: string | null
  templateId: number | null
  templateCode: string | null
  templateVersion: number | null
  mimeType: string
  sizeBytes: number
  createdAt: string
  uploadedById?: number
}

export interface TemplateSummary {
  id: number
  code: string
  name: string
  kind: string
  version: number
  language: string
}

export interface TemplateOption {
  kind: string
  outcome: 'selected' | 'ambiguous'
  template: TemplateSummary | null
  candidates: TemplateSummary[]
}

export interface MissingField {
  code: string
  label: string
}

export interface DocumentPreview {
  html: string
  missing: MissingField[]
  template: TemplateSummary
}

export const DOCUMENT_TEMPLATE_STATUS_LABELS: Record<DocumentTemplateStatus, string> = {
  Bozza: 'Bozza',
  Attivo: 'Attivo',
  Archiviato: 'Archiviato',
}

export const documentTemplatesApi = {
  fields: () => api<DocumentMergeField[]>('/api/document-templates/fields'),
  list: (params?: { status?: DocumentTemplateStatus; kind?: string }) => {
    const qs = new URLSearchParams()
    if (params?.status) qs.set('status', params.status)
    if (params?.kind) qs.set('kind', params.kind)
    const q = qs.toString()
    return api<DocumentTemplate[]>(`/api/document-templates${q ? `?${q}` : ''}`)
  },
  get: (id: number) => api<DocumentTemplate>(`/api/document-templates/${id}`),
  create: (body: DocumentTemplateBody) =>
    api<DocumentTemplate>('/api/document-templates', { method: 'POST', body: JSON.stringify(body) }),
  update: (id: number, body: DocumentTemplateBody) =>
    api<DocumentTemplate>(`/api/document-templates/${id}`, { method: 'PUT', body: JSON.stringify(body) }),
  activate: (id: number) => api<DocumentTemplate>(`/api/document-templates/${id}/activate`, { method: 'POST' }),
  archive: (id: number) => api<DocumentTemplate>(`/api/document-templates/${id}/archive`, { method: 'POST' }),
  newVersion: (id: number, reason: string) =>
    api<DocumentTemplate>(`/api/document-templates/${id}/new-version`, {
      method: 'POST',
      body: JSON.stringify({ reason }),
    }),
}

export const generatedDocumentsApi = {
  list: (contractId: number) => api<GeneratedDocument[]>(`/api/contracts/${contractId}/generated-documents`),
  templates: (contractId: number) =>
    api<TemplateOption[]>(`/api/contracts/${contractId}/generated-documents/templates`),
  preview: (contractId: number, body: { kind?: string; templateId?: number }) =>
    api<DocumentPreview>(`/api/contracts/${contractId}/generated-documents/preview`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  /** Una Idempotency-Key per tentativo: il doppio clic o il retry non creano un doppione. */
  generate: (contractId: number, kind: string, idempotencyKey: string) =>
    api<GeneratedDocument>(
      `/api/contracts/${contractId}/generated-documents/${encodeURIComponent(kind)}`,
      withIdempotencyKey({ method: 'POST' }, idempotencyKey),
    ),
}
